const STALE_SHELL_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Failed to load module script/i,
  /MIME type of "text\/html"/i,
  /supported stylesheet MIME type/i,
] as const;

type RegisterSWFn = (options?: {
  immediate?: boolean;
  onNeedRefresh?: () => void;
}) => (() => Promise<void>) | void;

type WindowLike = {
  addEventListener: Window["addEventListener"];
  location: { reload: () => void };
  sessionStorage: Pick<Storage, "getItem" | "setItem">;
};

interface InstallPwaUpdateRecoveryOptions {
  buildId?: string;
  register?: RegisterSWFn;
  windowObject?: WindowLike;
}

export function installPwaUpdateRecovery({
  buildId = import.meta.env.VITE_COMMIT_HASH || "unknown",
  register = () => undefined,
  windowObject = window,
}: InstallPwaUpdateRecoveryOptions): void {
  let updateServiceWorker: ReturnType<RegisterSWFn> | undefined;

  const recover = () => {
    if (typeof updateServiceWorker === "function") {
      void updateServiceWorker();
    }
    reloadOncePerBuild(windowObject, buildId);
  };

  updateServiceWorker = register({
    immediate: true,
    onNeedRefresh: recover,
  });

  windowObject.addEventListener("error", (event) => {
    if (isStaleShellFailure(event)) {
      recover();
    }
  });

  windowObject.addEventListener("unhandledrejection", (event) => {
    if (isStaleShellFailure(event)) {
      recover();
    }
  });
}

function isStaleShellFailure(event: unknown): boolean {
  const message = extractMessage(event);

  return STALE_SHELL_PATTERNS.some((pattern) => pattern.test(message));
}

function extractMessage(input: unknown): string {
  if (typeof input === "string") {
    return input;
  }

  if (input instanceof Error) {
    return `${input.name}: ${input.message}`;
  }

  if (typeof input === "object" && input !== null) {
    const candidate = input as {
      message?: unknown;
      reason?: unknown;
      error?: unknown;
    };

    return [
      extractMessage(candidate.message),
      extractMessage(candidate.reason),
      extractMessage(candidate.error),
    ]
      .filter(Boolean)
      .join(" ");
  }

  return "";
}

function reloadOncePerBuild(windowObject: WindowLike, buildId: string): void {
  const storageKey = `nokosha:pwa-reload:${buildId}`;

  if (windowObject.sessionStorage.getItem(storageKey) === "1") {
    return;
  }

  windowObject.sessionStorage.setItem(storageKey, "1");
  windowObject.location.reload();
}
